import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  PlusIcon, MinusIcon, TrashIcon, PrinterIcon, ForkKnifeIcon, PackageIcon,
  ArrowLeftIcon, CheckCircleIcon, ReceiptIcon,
} from "@phosphor-icons/react";
import { inr, isOutOfStock, CATEGORY_ICON, CATEGORY_TONE } from "./posHelpers";

export function TableOrderPanel({ activeId, tableOrder, menu, platform, onClose, onChange }) {
  const [detail, setDetail] = useState(null);
  const [cart, setCart] = useState([]);
  const [catFilter, setCatFilter] = useState("all");
  const [closing, setClosing] = useState(false);
  const [paymentMode, setPaymentMode] = useState("cash");
  const [discount, setDiscount] = useState(0);
  const [discountType, setDiscountType] = useState("amount"); // amount | percent
  const [occupiedRooms, setOccupiedRooms] = useState([]);
  const [selectedRoomId, setSelectedRoomId] = useState("");

  const load = () => api.get(`/tables/${tableOrder.id}/detail`).then((r) => setDetail(r.data));
  useEffect(() => {
    load();
    api.get("/pos/occupied-rooms", { params: { property_id: activeId } })
      .then((r) => setOccupiedRooms((r.data || []).filter((x) => x.is_checked_in)))
      .catch(() => setOccupiedRooms([]));
    /* eslint-disable-next-line */
  }, [tableOrder.id]);

  const addToCart = (mi) => {
    if (isOutOfStock(mi)) return toast.error(`${mi.name} is out of stock`);
    setCart((c) => {
      const ex = c.find((x) => x.menu_item.id === mi.id);
      if (ex) return c.map((x) => x.menu_item.id === mi.id ? { ...x, qty: x.qty + 1 } : x);
      return [...c, { menu_item: mi, qty: 1 }];
    });
  };
  const bump = (id, d) => setCart((c) => c.map((x) => x.menu_item.id === id ? { ...x, qty: Math.max(0, x.qty + d) } : x).filter((x) => x.qty > 0));

  const cartTotals = useMemo(() => {
    let sub = 0, tax = 0;
    cart.forEach((c) => {
      const s = c.menu_item.price * c.qty;
      sub += s; tax += s * c.menu_item.gst_rate / 100;
    });
    return { sub: +sub.toFixed(2), tax: +tax.toFixed(2), grand: +(sub + tax).toFixed(2) };
  }, [cart]);

  const sendToKitchen = async () => {
    if (cart.length === 0) return toast.error("Cart is empty");
    try {
      const r = await api.post("/pos/orders", {
        property_id: activeId,
        table_order_id: tableOrder.id,
        items: cart.map((c) => ({
          menu_item_id: c.menu_item.id,
          name: c.menu_item.unit_type === "peg" ? `${c.menu_item.name} (${c.menu_item.peg_size_ml}ml peg)` : c.menu_item.name,
          price: c.menu_item.price,
          gst_rate: c.menu_item.gst_rate,
          qty: c.qty,
        })),
        payment_mode: "cash", // placeholder until table is closed
        discount: 0,
        order_type: "dine_in",
      });
      toast.success(`Round ${r.data.order_no} sent`);
      // Auto-print KOT if enabled
      if (platform?.printer_auto_print_kot) {
        window.open(`/pos/print/kot/${r.data.id}`, "kotPrint", "width=380,height=600");
      }
      setCart([]);
      await load();
      onChange?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };

  const printKot = (roundId) => window.open(`/pos/print/kot/${roundId}`, "kotPrint", "width=380,height=600");

  const closeAndBill = async () => {
    // Allow closing a table that never received an order — just free the
    // table without generating a phantom bill.
    const isEmpty = !detail?.rounds?.length;
    if (isEmpty) {
      if (!window.confirm("This table has no rounds. Close it without generating a bill?\n\nStock is untouched; the table will be marked as cancelled.")) return;
    } else if (paymentMode === "room_charge") {
      if (!selectedRoomId) return toast.error("Select a checked-in room to post the bill to");
    }
    const room = occupiedRooms.find((r) => r.room_id === selectedRoomId);
    setClosing(true);
    try {
      const body = {
        payment_mode: isEmpty ? "cash" : paymentMode,
        discount: Number(discount) || 0,
        discount_type: discountType,
      };
      if (!isEmpty && paymentMode === "room_charge" && room) {
        body.reservation_id = room.reservation_id;
        body.guest_id = room.guest_id;
        body.room_id = room.room_id;
      }
      const r = await api.post(`/tables/${tableOrder.id}/close`, body);
      if (r.data.cancelled) {
        toast.success(`Table ${tableOrder.table_number} freed — no bill generated`);
      } else if (r.data.posted_to_reservation) {
        toast.success(`Bill posted to Room ${room?.room_number} — will settle on check-out`);
      } else {
        toast.success("Table closed & billed");
        window.open(`/pos/print/receipt/${tableOrder.id}`, "receipt", "width=380,height=700");
      }
      onChange?.();
      onClose?.();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
    finally { setClosing(false); }
  };

  const filteredMenu = catFilter === "all" ? menu : menu.filter((m) => m.category === catFilter);
  const categories = ["all", ...Array.from(new Set(menu.map((m) => m.category)))];
  const runningTotal = detail?.running_total || 0;
  const runningSub = detail?.running_subtotal || 0;
  const runningTax = detail?.running_tax || 0;
  const grossPreDisc = runningSub + runningTax + cartTotals.sub + cartTotals.tax;
  const discountValue = discountType === "percent"
    ? Math.max(0, Math.min(100, Number(discount) || 0)) * grossPreDisc / 100
    : Number(discount) || 0;
  const finalGrand = Math.max(0, grossPreDisc - discountValue);

  return (
    <div className="fixed inset-0 bg-black/60 z-40 flex items-stretch justify-end" onClick={onClose}>
      <div className="bg-background w-full max-w-6xl overflow-y-auto shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="table-order-panel">
        {/* Header */}
        <div className="sticky top-0 bg-background border-b border-border p-4 flex items-center justify-between z-10">
          <div className="flex items-center gap-3">
            <Button size="icon" variant="ghost" onClick={onClose}><ArrowLeftIcon size={18} /></Button>
            <div>
              <p className="label-overline">Table {tableOrder.table_number} · {tableOrder.section}</p>
              <h2 className="font-display text-2xl leading-none">
                Bill — {inr(runningTotal + cartTotals.grand)}
              </h2>
              <p className="text-xs text-muted-foreground">
                {tableOrder.guest_count} guest{tableOrder.guest_count === 1 ? "" : "s"} · opened {new Date(tableOrder.opened_at).toLocaleTimeString("en-IN")}
                {tableOrder.customer_name ? ` · ${tableOrder.customer_name}` : ""}
              </p>
            </div>
          </div>
          <Badge variant="outline" className="bg-[hsl(38_92%_92%)] text-[hsl(38_92%_25%)] border-[hsl(38_92%_55%)]">OPEN</Badge>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 p-4">
          {/* Menu */}
          <div className="lg:col-span-2 space-y-3">
            <div className="flex flex-wrap gap-1.5">
              {categories.map((c) => (
                <button
                  key={c}
                  onClick={() => setCatFilter(c)}
                  className={`px-2.5 py-1 rounded-full text-[11px] uppercase tracking-wider border transition-colors ${catFilter === c ? "border-primary bg-primary/10 text-primary" : "border-border text-muted-foreground hover:border-primary/50"}`}
                  data-testid={`table-cat-${c}`}
                >
                  {c === "all" ? `All (${menu.length})` : c}
                </button>
              ))}
            </div>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
              {filteredMenu.filter((m) => m.is_active).map((mi) => {
                const Icon = CATEGORY_ICON[mi.category] || PackageIcon;
                const oos = isOutOfStock(mi);
                return (
                  <button
                    key={mi.id}
                    onClick={() => addToCart(mi)}
                    disabled={oos}
                    className={`text-left p-2 rounded-lg border transition-all ${oos ? "opacity-40 cursor-not-allowed" : "hover:border-primary hover:shadow-md"} border-border bg-card`}
                    data-testid={`table-menu-${mi.id}`}
                  >
                    <div className="flex items-start justify-between">
                      <Icon size={16} weight="duotone" className="text-primary" />
                      <Badge variant="outline" className={`text-[9px] ${CATEGORY_TONE[mi.category] || ""}`}>{mi.category}</Badge>
                    </div>
                    <p className="font-medium mt-1 text-xs truncate">{mi.name}</p>
                    <div className="flex items-baseline justify-between mt-0.5">
                      <p className="font-display text-sm">{inr(mi.price)}{mi.unit_type === "peg" && <span className="text-[9px] text-muted-foreground"> /peg</span>}</p>
                      <p className="text-[9px] text-muted-foreground">{mi.gst_rate}%</p>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          {/* Right side: pending round + prior rounds + close bill */}
          <div className="space-y-3">
            {/* Pending round */}
            <Card className="p-3">
              <div className="flex items-center justify-between">
                <p className="label-overline">Pending round</p>
                <Badge variant="outline" className="text-[10px]">{cart.length} items</Badge>
              </div>
              {cart.length === 0 && <p className="text-xs text-muted-foreground italic mt-2">Tap menu items to add.</p>}
              <div className="mt-2 space-y-1.5 max-h-56 overflow-y-auto">
                {cart.map((c) => (
                  <div key={c.menu_item.id} className="flex items-center gap-1.5 text-xs border-b border-border/50 pb-1.5" data-testid={`table-cart-${c.menu_item.id}`}>
                    <div className="flex-1 truncate">
                      <p className="truncate">{c.menu_item.name}</p>
                      <p className="text-[10px] text-muted-foreground">{inr(c.menu_item.price)} · {c.menu_item.gst_rate}%</p>
                    </div>
                    <div className="flex items-center gap-0.5">
                      <Button size="icon" variant="outline" className="h-5 w-5" onClick={() => bump(c.menu_item.id, -1)}><MinusIcon size={8} /></Button>
                      <span className="font-mono-plex w-4 text-center">{c.qty}</span>
                      <Button size="icon" variant="outline" className="h-5 w-5" onClick={() => bump(c.menu_item.id, 1)}><PlusIcon size={8} /></Button>
                    </div>
                  </div>
                ))}
              </div>
              {cart.length > 0 && (
                <>
                  <div className="border-t border-border mt-2 pt-2 flex justify-between text-xs">
                    <span className="text-muted-foreground">Round total</span>
                    <span className="font-mono-plex font-semibold">{inr(cartTotals.grand)}</span>
                  </div>
                  <Button className="w-full mt-2" onClick={sendToKitchen} data-testid="table-send-kitchen">
                    <ForkKnifeIcon size={14} className="mr-1" /> Send to kitchen
                  </Button>
                </>
              )}
            </Card>

            {/* Prior rounds */}
            <Card className="p-3">
              <p className="label-overline">Rounds sent ({detail?.rounds?.length || 0})</p>
              <div className="mt-2 space-y-2 max-h-64 overflow-y-auto">
                {(detail?.rounds || []).map((r) => (
                  <div key={r.id} className="rounded border border-border/60 p-2" data-testid={`table-round-${r.order_no}`}>
                    <div className="flex justify-between items-center">
                      <p className="text-xs font-mono-plex">{r.order_no}</p>
                      <div className="flex items-center gap-1">
                        <span className="text-[10px] text-muted-foreground">{new Date(r.created_at).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" })}</span>
                        <Button size="icon" variant="ghost" className="h-6 w-6" onClick={() => printKot(r.id)} data-testid={`table-print-kot-${r.order_no}`}>
                          <PrinterIcon size={12} />
                        </Button>
                      </div>
                    </div>
                    <ul className="mt-1 space-y-0.5">
                      {(r.items || []).map((it, i) => (
                        <li key={i} className="text-[11px] flex justify-between">
                          <span>{it.qty}× {it.name}</span>
                          <span className="font-mono-plex text-muted-foreground">{inr(it.line_subtotal || it.price * it.qty)}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                ))}
                {!detail?.rounds?.length && <p className="text-xs italic text-muted-foreground">No rounds yet.</p>}
              </div>
              <div className="border-t border-border mt-2 pt-2 space-y-0.5 text-xs">
                <div className="flex justify-between"><span className="text-muted-foreground">Subtotal (sent)</span><span className="font-mono-plex">{inr(runningSub)}</span></div>
                <div className="flex justify-between"><span className="text-muted-foreground">GST (sent)</span><span className="font-mono-plex">{inr(runningTax)}</span></div>
                <div className="flex justify-between border-t border-border/50 pt-1 mt-1 font-semibold">
                  <span>Bill so far</span>
                  <span className="font-mono-plex">{inr(runningTotal)}</span>
                </div>
              </div>
            </Card>

            {/* Close & bill */}
            <Card className="p-3 border-primary/40">
              <p className="label-overline">Close & bill</p>
              <div className="mt-2 space-y-2">
                <div>
                  <Label className="text-xs">Payment mode</Label>
                  <Select value={paymentMode} onValueChange={setPaymentMode}>
                    <SelectTrigger className="h-8 text-xs mt-1" data-testid="table-close-payment"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="cash">Cash</SelectItem>
                      <SelectItem value="upi">UPI</SelectItem>
                      <SelectItem value="card">Card</SelectItem>
                      <SelectItem value="room_charge">Post to room (hotel guest)</SelectItem>
                      <SelectItem value="other">Other</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                {paymentMode === "room_charge" && (
                  <div className="rounded-md border border-primary/40 bg-primary/5 p-2 space-y-1">
                    <Label className="text-[10px]">Checked-in room</Label>
                    <Select value={selectedRoomId} onValueChange={setSelectedRoomId}>
                      <SelectTrigger className="h-8 text-xs" data-testid="table-close-room-select"><SelectValue placeholder="Pick room…" /></SelectTrigger>
                      <SelectContent>
                        {occupiedRooms.length === 0 && (
                          <SelectItem value="__none" disabled>No checked-in rooms available</SelectItem>
                        )}
                        {occupiedRooms.map((r) => (
                          <SelectItem key={r.room_id} value={r.room_id} data-testid={`table-close-room-${r.room_number}`}>
                            #{r.room_number} · {r.guest_name} · out {r.check_out}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <p className="text-[10px] text-muted-foreground">Only currently checked-in rooms are shown. The entire table bill will be added to the room folio and settled at check-out with one consolidated invoice.</p>
                  </div>
                )}
                <div>
                  <Label className="text-xs">Discount</Label>
                  <div className="flex gap-1 mt-1">
                    <Input
                      type="number"
                      value={discount}
                      onChange={(e) => setDiscount(e.target.value)}
                      className="h-8 text-xs flex-1"
                      data-testid="table-close-discount"
                    />
                    <div className="flex rounded-md border border-border overflow-hidden">
                      <button
                        type="button"
                        onClick={() => setDiscountType("amount")}
                        className={`px-2 text-xs ${discountType === "amount" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                        data-testid="table-close-discount-amount"
                      >₹</button>
                      <button
                        type="button"
                        onClick={() => setDiscountType("percent")}
                        className={`px-2 text-xs ${discountType === "percent" ? "bg-primary text-primary-foreground" : "hover:bg-muted"}`}
                        data-testid="table-close-discount-percent"
                      >%</button>
                    </div>
                  </div>
                  {discountType === "percent" && (Number(discount) || 0) > 0 && (
                    <p className="text-[10px] text-muted-foreground mt-1">= {inr(discountValue)} off</p>
                  )}
                </div>
                <div className="flex justify-between pt-2 border-t border-border/50 text-sm">
                  <span className="font-medium">Final total</span>
                  <span className="font-display text-xl">{inr(finalGrand)}</span>
                </div>
                <Button
                  className="w-full mt-1"
                  onClick={closeAndBill}
                  disabled={closing}
                  variant={!detail?.rounds?.length ? "outline" : "default"}
                  data-testid="table-close-submit"
                >
                  <ReceiptIcon size={14} className="mr-1" />
                  {!detail?.rounds?.length
                    ? "Close empty table (no bill)"
                    : paymentMode === "room_charge"
                    ? "Post to room folio"
                    : "Close & print bill"}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
